// Wave 11 — Ad-Account Connections.
// Wave Phase-2 #14b — OAuth connect flow for FB / Google Ads.
//
// Operator-facing CRUD over `/api/ad-account-connections`. Supports
// both manual token entry (mock / dev) and OAuth-based connection
// (Facebook, Google Ads) when server-side OAuth is configured.

import React, { useEffect, useState, useCallback } from 'react';
import ConfirmDialog from './ConfirmDialog';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Plus, Trash2, RefreshCw, ExternalLink, Pencil } from 'lucide-react';
import AdAccountEditDialog from './AdAccountEditDialog';

const PROVIDERS = ['facebook', 'google_ads', 'mock'];

const EMPTY_FORM = {
  provider: 'mock',
  account_id: '',
  name: '',
  access_token: '',
  refresh_token: '',
};

export default function AdAccountConnections() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [syncing, setSyncing] = useState(false);
  const [oauthProviders, setOauthProviders] = useState([]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ad-account-connections`);
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Failed to load connections'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadOauthProviders = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ad-accounts/oauth/providers`);
      setOauthProviders((r.data?.providers || []).map((p) => p.provider));
    } catch {
      // OAuth not configured — manual-only mode
    }
  }, []);

  useEffect(() => {
    reload();
    loadOauthProviders();
  }, [reload, loadOauthProviders]);

  // Handle OAuth callback hash params (success/error)
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const params = new URLSearchParams(hash.substring(1));
    if (params.get('success') === 'true') {
      toast.success(t('Ad account connected via OAuth'));
      reload();
      window.history.replaceState(null, '', window.location.pathname);
    } else if (params.get('error')) {
      const err = params.get('error');
      const desc = params.get('error_description');
      toast.error(desc || t('OAuth error: ') + err);
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [reload, t]);

  const submit = async () => {
    try {
      await axios.post(`${API}/ad-account-connections`, form);
      toast.success(t('Connection created'));
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Save failed'));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const remove = async (row) => {
    try {
      await axios.delete(`${API}/ad-account-connections/${row.id}`);
      setDeleteTarget(null);
      toast.success(t('Connection deleted'));
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Delete failed'));
    }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/ad-account-connections/sync`);
      toast.success(
        t('Sync complete') +
          ` — ${r.data?.connections ?? 0} ${t('connections')}, ${r.data?.rows ?? 0} ${t('rows')}`,
      );
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Sync failed'));
    } finally {
      setSyncing(false);
    }
  };

  const startOAuth = async (provider) => {
    try {
      const r = await axios.get(`${API}/ad-accounts/oauth/${provider}/login`);
      if (r.data?.authorization_url) {
        window.location.href = r.data.authorization_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Failed to start OAuth'));
    }
  };

  const refreshToken = async (row) => {
    try {
      await axios.post(
        `${API}/ad-accounts/oauth/${row.provider}/refresh?connection_id=${row.id}`,
      );
      toast.success(t('Token refreshed'));
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Refresh failed'));
    }
  };

  const providerLabel = (p) => {
    if (p === 'facebook') return 'Facebook';
    if (p === 'google_ads') return 'Google Ads';
    return p;
  };

  return (
    <div className="xp-page">
      <Card data-testid="ad-account-connections-card">
        <CardHeader className="flex flex-row items-start justify-between gap-2">
          <div>
            <CardTitle>{t('Ad-Account Connections')}</CardTitle>
            <CardDescription>
              {t(
                'Connect FB / Google Ads accounts so cost data is pulled into campaigns automatically.',
              )}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={triggerSync}
              disabled={syncing}
              data-testid="trigger-cost-sync"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
              {t('Sync now')}
            </Button>
            <Button
              onClick={() => {
                setForm(EMPTY_FORM);
                setDialogOpen(true);
              }}
              data-testid="new-connection"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('Connect')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {oauthProviders.length > 0 && (
            <div className="flex gap-2 mb-4" data-testid="oauth-buttons">
              {oauthProviders.includes('facebook') && (
                <Button
                  variant="outline"
                  onClick={() => startOAuth('facebook')}
                  data-testid="oauth-facebook"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  {t('Connect with Facebook')}
                </Button>
              )}
              {oauthProviders.includes('google_ads') && (
                <Button
                  variant="outline"
                  onClick={() => startOAuth('google_ads')}
                  data-testid="oauth-google-ads"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  {t('Connect with Google Ads')}
                </Button>
              )}
            </div>
          )}
          <Table data-testid="connections-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('Provider')}</TableHead>
                <TableHead>{t('Account')}</TableHead>
                <TableHead>{t('Last sync')}</TableHead>
                <TableHead>{t('Status')}</TableHead>
                <TableHead className="text-right">{t('Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id} data-testid={`connection-row-${row.id}`}>
                  <TableCell>
                    <Badge variant="outline">{providerLabel(row.provider)}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{row.name || row.account_id}</div>
                    <div className="text-xs text-muted-foreground">
                      {row.account_id}
                    </div>
                  </TableCell>
                  <TableCell>
                    {row.last_sync ? new Date(row.last_sync).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell>
                    {row.is_active ? (
                      <Badge>{t('Active')}</Badge>
                    ) : (
                      <Badge variant="secondary">{t('Disabled')}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      {(row.provider === 'facebook' || row.provider === 'google_ads') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => refreshToken(row)}
                          aria-label={t('Refresh token')}
                          title={t('Refresh token')}
                          data-testid={`refresh-connection-${row.id}`}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t('Edit item')}
                        title={t('Edit item')}
                        onClick={() => setEditTarget(row)}
                        data-testid={`edit-connection-${row.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t('Delete connection')}
                        title={t('Delete connection')}
                        onClick={() => setDeleteTarget(row)}
                        data-testid={`delete-connection-${row.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    {t(
                      'No connections yet. Connect FB or Google Ads to start importing cost.',
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setForm(EMPTY_FORM); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('Connect ad account')}</DialogTitle>
            <DialogDescription>
              {t(
                'Tokens are encrypted at rest. Use OAuth buttons above for FB / Google, or enter tokens manually below.',
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>{t('Provider')}</Label>
              <Select
                value={form.provider}
                onValueChange={(v) => setForm((f) => ({ ...f, provider: v }))}
              >
                <SelectTrigger data-testid="connection-provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROVIDERS.map((p) => (
                    <SelectItem key={p} value={p}>
                      {providerLabel(p)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('Account ID')}</Label>
              <Input
                data-testid="connection-account-id"
                value={form.account_id}
                onChange={(e) =>
                  setForm((f) => ({ ...f, account_id: e.target.value }))
                }
              />
            </div>
            <div>
              <Label>{t('Display name')}</Label>
              <Input
                data-testid="connection-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>{t('Access token')}</Label>
              <Input
                data-testid="connection-access-token"
                type="password"
                value={form.access_token}
                onChange={(e) =>
                  setForm((f) => ({ ...f, access_token: e.target.value }))
                }
              />
            </div>
            <div>
              <Label>{t('Refresh token (optional)')}</Label>
              <Input
                data-testid="connection-refresh-token"
                type="password"
                value={form.refresh_token}
                onChange={(e) =>
                  setForm((f) => ({ ...f, refresh_token: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button onClick={submit} data-testid="save-connection">
              {t('Connect')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AdAccountEditDialog
        connection={editTarget}
        open={!!editTarget}
        onOpenChange={(open) => { if (!open) setEditTarget(null); }}
        onSaved={(upd) => upd && setItems((prev) => prev.map((x) => (x.id === upd.id ? { ...x, ...upd } : x)))}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t('Delete connection')}
        description={t('This will permanently delete this ad account connection. Cost syncing will stop.')}
        confirmLabel={t('Delete')}
        onConfirm={() => remove(deleteTarget)}
      />
    </div>
  );
}
